'use client';

import {
    ExhibitionsDetailHeroFrame,
    ExhibitionsDetailSectionFrame
} from "@/app/features/sites/exhibitions/ui/shared/ExhibitionsDetailFrames";
import SnapSectionsContainer from "@/app/features/shared/snap-section/SnapSectionsContainer";
import SlideshowIntro from "@/app/__to_be_integrated/_____components/_NEW/intros/slideshow/SlideshowIntro";
import IntroAnchor from "@/app/features/shared/__to_be_integrated/ui/IntroAnchor";
import { Box, Divider } from "@mui/material";
import Section from "@/app/features/shared/snap-section/Section";
import { DetailSectionEditable } from "@/app/features/shared/detail-section";
import ShowroomEditable from "@/app/features/shared/showroom/ui/editable/Showroom.editable";
import RelatedInfosView from "@/app/features/shared/related-infos/ui/RelatedInfos.public";
import { notNull } from "@/app/lib/common/not-null";
import {
    useSiteExhibitionDetailEditor
} from "@/app/features/sites/exhibitions/contexts/SiteExhibitionDetailEditorContext";
import { ExhibitionDetailEditable as ExhibitionDetailEditableType } from "@/app/features/sites/exhibitions/types/types";
import { DetailEditable } from "@/app/features/shared/types/types";
import { RelatedInfo } from "@/app/lib/generated/graphql-types";
import type { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { PLACEHOLDER_IMAGE } from "@/app/types/common/constants";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

const MemoizedSlideshowIntro = memo(SlideshowIntro, (previous, next) => (
    previous.title === next.title
    && previous.subtitle === next.subtitle
    && previous.isEditing === next.isEditing
    && previous.textCommitMode === next.textCommitMode
    && previous.onTitleChanged === next.onTitleChanged
    && previous.onSubtitleChanged === next.onSubtitleChanged
    && previous.images.length === next.images.length
    && previous.images.every((image, index) => image === next.images[index])
));
const MemoizedShowroomEditable = memo(ShowroomEditable);
const MemoizedRelatedInfosView = memo(RelatedInfosView);

export default function ExhibitionDetailEditable() {
    const {
        editable,
        updateEditable,
    } = useSiteExhibitionDetailEditor();
    const showroom = editable?.showroom;

    const details = useMemo(() => editable?.details?.filter(notNull) ?? [], [editable?.details]);
    const detailsRef = useRef(details);
    useEffect(() => {
        detailsRef.current = details;
    }, [details]);

    const promoImages = useMemo(() => details
        .map((detail) => detail.image)
        .filter((image): image is MediaRef => image != null), [details]);

    const updateDetailByIndex = useCallback((indexToUpdate: number, partialDetail: Partial<DetailEditable>) => {
        updateEditable({
            details: detailsRef.current.map((detail, index) => {
                return index === indexToUpdate
                    ? { ...detail, ...partialDetail }
                    : detail
            }) as ExhibitionDetailEditableType['details']
        });
    }, [updateEditable]);

    const updateTitle = useCallback((title: string) => updateEditable({ title }), [updateEditable]);
    const updateSubtitle = useCallback((subtitle: string) => updateEditable({ subtitle }), [updateEditable]);
    const updateShowroom = useCallback((partialShowroom: Partial<NonNullable<ExhibitionDetailEditableType['showroom']>>) => {
        if (!showroom) return;

        updateEditable({
            showroom: {
                ...showroom,
                ...partialShowroom,
            },
        });
    }, [showroom, updateEditable]);

    if (!editable) return null;

    return (
        <SnapSectionsContainer>
            <ExhibitionsDetailHeroFrame>
                <MemoizedSlideshowIntro
                    title={editable.title}
                    subtitle={editable.subtitle || undefined}
                    images={promoImages}
                    isEditing
                    textCommitMode="blur"
                    onTitleChanged={updateTitle}
                    onSubtitleChanged={updateSubtitle}
                />
            </ExhibitionsDetailHeroFrame>

            <ExhibitionsDetailSectionFrame>
                <IntroAnchor />

                {details.map((detail, index) => (
                    <ExhibitionDetailSectionEditor
                        key={detail.id ?? index}
                        detail={detail as DetailEditable}
                        index={index}
                        onChange={updateDetailByIndex}
                    />
                ))}

                {editable.showroom && (
                    <Section contentHeight="fit-content">
                        <MemoizedShowroomEditable
                            data={editable.showroom}
                            onTitleChange={(title) => updateShowroom({ title })}
                            onSubtitleChange={(subtitle) => updateShowroom({ subtitle })}
                            onDescriptionChange={(description) => updateShowroom({ description })}
                            onImagesChange={(images) => updateShowroom({ images })}
                        />
                    </Section>
                )}

                {editable.relatedInfos && editable.relatedInfos.length > 0 && (
                    <MemoizedRelatedInfosView
                        title="Möchten Sie mehr erfahren?"
                        relatedInfos={editable.relatedInfos as RelatedInfo[]}
                    />
                )}
            </ExhibitionsDetailSectionFrame>
        </SnapSectionsContainer>
    )
}

const ExhibitionDetailSectionEditor = memo(function ExhibitionDetailSectionEditor({
    detail,
    index,
    onChange,
}: {
    detail: DetailEditable;
    index: number;
    onChange: (index: number, partialDetail: Partial<DetailEditable>) => void;
}) {
    const updateTitle = useCallback((title: string) => onChange(index, { title }), [index, onChange]);
    const updateSubtitle = useCallback((subtitle: string) => onChange(index, { subtitle }), [index, onChange]);
    const updateDescription = useCallback((description: string) => onChange(index, { description }), [index, onChange]);
    const updateImage = useCallback(
        (image: MediaRef | null) => onChange(index, { image: image || PLACEHOLDER_IMAGE }),
        [index, onChange]
    );

    return (
        <div>
            <Box sx={{ py: 4, px: '5%' }}>
                <Divider />
            </Box>
            <Section>
                <DetailSectionEditable
                    data={detail}
                    alignment={index % 2 === 0 ? "row" : "row-reverse"}
                    textCommitMode="blur"
                    onTitleChange={updateTitle}
                    onSubtitleChange={updateSubtitle}
                    onDescriptionChange={updateDescription}
                    onImageChange={updateImage}
                />
            </Section>
        </div>
    );
});
